/**
 * Sharing utilities for generating and sharing images/videos of the heatmap
 */

import { config } from '../config/environment';
import { sendTelemetryEvent } from '../services/telemetryService';

interface HeatmapData {
  matrix: number[][];
}

interface TimeRangeOption {
  key: string;
  label: string;
  description: string;
}

interface MusicAnalysis {
  quadrant: string;
  diversity: string;
  diversityDescription: string;
  description: string;
  avgValence: number;
  avgEnergy: number;
  spread: number;
}

/**
 * Get optimal export dimensions based on device and format
 */
export const getExportDimensions = (format: 'image' | 'video' = 'image') => {
  const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
  
  if (isMobile && format === 'image') {
    // Instagram story dimensions (9:16 aspect ratio) - high res
    return {
      width: 1080,
      height: 1920
    };
  }
  
  // Desktop: larger dimensions for better quality
  return {
    width: 1600,
    height: 2000 // Will be recalculated
  };
};

/**
 * Generate and share/download an image
 */
export const generateImage = async (
  currentTimeRange: string,
  timeRangeOptions: TimeRangeOption[],
  sourceCanvas: HTMLCanvasElement,
  user?: { name: string; imageUrl: string } | null,
  analysis?: MusicAnalysis | null,
  features?: any[],
  action: 'download' | 'share' = 'share',
  platform?: 'instagram',
  viewMode?: 'time-period' | 'playlist',
  playlistName?: string,
  playlistImage?: string,
  playlistOwner?: string,
  musicPlatform?: string,
  darkMode?: boolean
): Promise<void> => {
  // Use Instagram dimensions if specified, otherwise use default
  const dimensions = platform === 'instagram' 
    ? { width: 1080, height: 1920 } // Instagram Story dimensions
    : getExportDimensions('image');
  
  let { width, height } = dimensions;
  
  const timeRangeLabel = timeRangeOptions.find(opt => opt.key === currentTimeRange)?.label || 'Your Music';
  
  // Calculate layout based on dimensions - calculate text first, use remainder for heatmap
  const isMobile = height > width;
  const padding = isMobile ? 60 : 100;
  
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  
  if (!ctx) throw new Error('Could not get canvas context');
  
  // Fill background
  ctx.fillStyle = darkMode ? '#0F0F23' : '#f8f8f8';
  ctx.fillRect(0, 0, width, height);
  
  // Set up fonts to measure text accurately
  const logoFontSize = isMobile ? 100 : 180;
  ctx.font = `900 ${logoFontSize}px Inter, Helvetica, Arial, sans-serif`;
  const logoMetrics = ctx.measureText('emotify');
  const logoActualHeight = logoMetrics.actualBoundingBoxAscent + logoMetrics.actualBoundingBoxDescent;
  const spaceAfterLogo = isMobile ? 90 : 130;
  
  const headlineFontSize = isMobile ? 48 : 64;
  const headlineLineHeight = isMobile ? 50 : 70;
  ctx.font = `600 ${headlineFontSize}px Inter, Helvetica, Arial, sans-serif`;
  const headlineMetrics = ctx.measureText("Emotional Heatmap for");
  const headlineActualHeight = headlineMetrics.actualBoundingBoxAscent + headlineMetrics.actualBoundingBoxDescent;
  const headlineHeight = headlineActualHeight;
  const spaceBelowHeadline = isMobile ? 60 : 90;
  
  const playlistInfoHeight = (playlistImage || playlistName) ? (isMobile ? 80 + 30 : 100 + 40) : 0; // cover + spacing
  const cardPadding = isMobile ? 40 : 70;
  const spaceBetweenSections = isMobile ? 90 : 140;
  
  const dominantMoodFontSize = isMobile ? 68 : 100;
  ctx.font = `700 ${dominantMoodFontSize}px Inter, Helvetica, Arial, sans-serif`;
  const dominantMoodMetrics = ctx.measureText('Dominant Mood: Melancholic');
  const dominantMoodActualHeight = dominantMoodMetrics.actualBoundingBoxAscent + dominantMoodMetrics.actualBoundingBoxDescent;
  const spaceAfterDominantMood = isMobile ? 65 : 95;
  
  const summaryFontSize = isMobile ? 36 : 56;
  const summaryLineHeight = isMobile ? 52 : 72;
  ctx.font = `400 ${summaryFontSize}px Inter, Helvetica, Arial, sans-serif`;
  const estimatedSummaryLines = 3;
  const summaryHeight = summaryLineHeight * estimatedSummaryLines;
  const spaceBeforFooter = isMobile ? 65 : 90;
  
  const footerFontSize = isMobile ? 34 : 52;
  ctx.font = `400 ${footerFontSize}px Inter, Helvetica, Arial, sans-serif`;
  const footerMetrics = ctx.measureText('Discover more at emotify.dev');
  const footerActualHeight = footerMetrics.actualBoundingBoxAscent + footerMetrics.actualBoundingBoxDescent;
  
  // Calculate total fixed height (everything except heatmap)
  const fixedHeight = 
    padding + // top padding
    logoActualHeight + 
    spaceAfterLogo +
    headlineHeight + 
    headlineLineHeight + // space after subtitle
    playlistInfoHeight + // playlist image + name + owner
    spaceBelowHeadline + // space before heatmap card
    cardPadding + // top card padding
    cardPadding + // bottom card padding
    spaceBetweenSections +
    dominantMoodActualHeight + 
    spaceAfterDominantMood +
    summaryHeight + 
    spaceBeforFooter +
    footerActualHeight +
    padding; // bottom padding
  
  // Calculate available space for heatmap
  const availableForHeatmap = height - fixedHeight;
  
  // Heatmap should be the min of available height or available width (with margins)
  const maxWidthForHeatmap = width - padding * 2 - cardPadding * 2;
  const heatmapSize = Math.min(maxWidthForHeatmap, availableForHeatmap);
  const heatmapCardWidth = heatmapSize + cardPadding * 2;
  const heatmapCardHeight = heatmapSize + cardPadding * 2;
  
  // Now calculate actual total height with real heatmap size
  const totalContentHeight = 
    logoActualHeight + 
    spaceAfterLogo +
    headlineHeight + 
    headlineLineHeight +
    playlistInfoHeight +
    spaceBelowHeadline +
    heatmapCardHeight + 
    spaceBetweenSections +
    dominantMoodActualHeight + 
    spaceAfterDominantMood +
    summaryHeight + 
    spaceBeforFooter +
    footerActualHeight;
  
  // Calculate available space and distribute it (60% top, 40% bottom for better visual balance)
  const totalAvailableSpace = height - totalContentHeight;
  const startY = totalAvailableSpace * 0.60;
  
  // Add "emotify" logo at top center
  let currentY = startY + logoFontSize * 0.85;
  ctx.fillStyle = darkMode ? '#F8FAFC' : '#0F172A';
  ctx.font = `900 ${logoFontSize}px Inter, Helvetica, Arial, sans-serif`;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText('emotify', width / 2, currentY);
  
  // "Emotional Heatmap for" subtitle below logo
  currentY += spaceAfterLogo;
  const heatmapTitleSize = isMobile ? 48 : 64;
  ctx.font = `600 ${heatmapTitleSize}px Inter, Helvetica, Arial, sans-serif`;
  ctx.fillStyle = darkMode ? '#94A3B8' : 'rgba(0, 0, 0, 0.7)';
  ctx.textAlign = 'center';
  ctx.fillText("Emotional Heatmap for", width / 2, currentY);

  // Playlist info below subtitle (image + name + owner, no border)
  currentY += isMobile ? 50 : 70;
  if (playlistImage || playlistName) {
    const coverSize = isMobile ? 80 : 100;
    const cardGap = isMobile ? 16 : 20;
    const nameFontSize = isMobile ? 36 : 48;
    const ownerFontSize = isMobile ? 22 : 28;
    const nameOwnerGap = isMobile ? 10 : 14;
    const iconSize = isMobile ? 20 : 26;
    const iconTextGap = isMobile ? 6 : 8;
    
    // Measure text to center the group (cover + text block)
    ctx.font = `600 ${nameFontSize}px Inter, Helvetica, Arial, sans-serif`;
    const nameWidth = ctx.measureText(playlistName || '').width;
    const totalWidth = (playlistImage ? coverSize + cardGap : 0) + nameWidth;
    const startX = (width - totalWidth) / 2;

    let textStartX = startX;

    // Draw album cover
    if (playlistImage) {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        await new Promise<void>((resolve) => {
          img.onload = () => resolve();
          img.onerror = () => resolve();
          img.src = playlistImage;
        });
        if (img.complete && img.naturalWidth > 0) {
          const imgX = startX;
          const imgY = currentY;
          ctx.save();
          ctx.beginPath();
          ctx.roundRect(imgX, imgY, coverSize, coverSize, 12);
          ctx.clip();
          ctx.drawImage(img, imgX, imgY, coverSize, coverSize);
          ctx.restore();
          textStartX = imgX + coverSize + cardGap;
        }
      } catch (e) {
        // Skip image on error
      }
    }

    // Line 1: Playlist name
    const hasOwner = playlistOwner && playlistOwner.length > 0;
    const nameY = currentY + (hasOwner ? coverSize * 0.35 : coverSize / 2);
    ctx.font = `600 ${nameFontSize}px Inter, Helvetica, Arial, sans-serif`;
    ctx.fillStyle = darkMode ? '#F8FAFC' : '#000';
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(playlistName || '', textStartX, nameY);
    
    // Line 2: [icon] playlist by [owner]
    if (hasOwner) {
      const ownerY = nameY + nameFontSize * 0.5 + nameOwnerGap + ownerFontSize * 0.5;
      
      // Draw platform icon
      ctx.save();
      const iconDrawY = ownerY - iconSize / 2;
      const scale = iconSize / 24;
      ctx.translate(textStartX, iconDrawY);
      ctx.scale(scale, scale);
      ctx.fillStyle = darkMode ? 'rgba(148,163,184,0.7)' : 'rgba(0,0,0,0.5)';
      ctx.beginPath();
      if (musicPlatform === 'apple') {
        // Glyph is authored on a 384x512 grid; centre it in the 24x24 icon box
        ctx.translate(2.25, 0);
        ctx.scale(24 / 512, 24 / 512);
        ctx.fill(new Path2D('M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z'));
      } else if (musicPlatform === 'ytmusic') {
        const p = new Path2D('M12 0C5.376 0 0 5.376 0 12s5.376 12 12 12 12-5.376 12-12S18.624 0 12 0zm0 19.104c-3.924 0-7.104-3.18-7.104-7.104S8.076 4.896 12 4.896s7.104 3.18 7.104 7.104-3.18 7.104-7.104 7.104zm0-13.332c-3.432 0-6.228 2.796-6.228 6.228S8.568 18.228 12 18.228s6.228-2.796 6.228-6.228S15.432 5.772 12 5.772zM9.684 15.54V8.46L16.2 12l-6.516 3.54z');
        ctx.fill(p);
      } else {
        const p = new Path2D('M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z');
        ctx.fill(p);
      }
      ctx.restore();
      
      // "playlist by [owner]" text after icon
      ctx.font = `400 ${ownerFontSize}px Inter, Helvetica, Arial, sans-serif`;
      ctx.fillStyle = darkMode ? 'rgba(148,163,184,0.7)' : 'rgba(0,0,0,0.5)';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'middle';
      ctx.fillText(`playlist by ${playlistOwner}`, textStartX + iconSize + iconTextGap, ownerY);
    }
    ctx.textBaseline = 'alphabetic';

    currentY += coverSize + (isMobile ? 30 : 40);
  }
  
  // Heatmap card - MUCH BIGGER, fill most of the space
  currentY += spaceBelowHeadline;
  const heatmapCardX = (width - heatmapCardWidth) / 2;
  
  // Draw card background with shadow
  ctx.shadowColor = darkMode ? 'rgba(0, 0, 0, 0.4)' : 'rgba(0, 0, 0, 0.12)';
  ctx.shadowBlur = 40;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 15;
  
  ctx.fillStyle = darkMode ? '#1B1B30' : '#ffffff';
  ctx.beginPath();
  ctx.roundRect(heatmapCardX, currentY, heatmapCardWidth, heatmapCardHeight, 32);
  ctx.fill();
  
  // Reset shadow
  ctx.shadowColor = 'transparent';
  ctx.shadowBlur = 0;
  ctx.shadowOffsetX = 0;
  ctx.shadowOffsetY = 0;
  
  // Draw the heatmap canvas inside the card
  const heatmapX = heatmapCardX + cardPadding;
  const heatmapY = currentY + cardPadding;
  
  ctx.save();
  ctx.beginPath();
  ctx.roundRect(heatmapX, heatmapY, heatmapSize, heatmapSize, 16);
  ctx.clip();
  ctx.drawImage(sourceCanvas, heatmapX, heatmapY, heatmapSize, heatmapSize);
  ctx.restore();
  
  // Draw border around heatmap
  ctx.strokeStyle = darkMode ? 'rgba(255,255,255,0.12)' : '#cccccc';
  ctx.lineWidth = 3;
  ctx.beginPath();
  ctx.roundRect(heatmapX, heatmapY, heatmapSize, heatmapSize, 20);
  ctx.stroke();

  // Draw crosshairs
  const centerX = heatmapX + heatmapSize / 2;
  const centerY = heatmapY + heatmapSize / 2;

  ctx.strokeStyle = darkMode ? 'rgba(255,255,255,0.2)' : '#999999';
  ctx.lineWidth = 2;
  ctx.setLineDash([8, 8]);
  
  ctx.beginPath();
  ctx.moveTo(centerX, heatmapY);
  ctx.lineTo(centerX, heatmapY + heatmapSize);
  ctx.stroke();
  
  ctx.beginPath();
  ctx.moveTo(heatmapX, centerY);
  ctx.lineTo(heatmapX + heatmapSize, centerY);
  ctx.stroke();
  
  ctx.setLineDash([]);
  
  // Add quadrant labels - positioned at corners with equal distance, text centered in pills
  const distance = isMobile ? 25 : 40;
  const labelFontSize = isMobile ? 26 : 38;
  const pillRadius = 12;
  const bgPadding = 14;
  ctx.font = `600 ${labelFontSize}px Inter, Helvetica, Arial, sans-serif`;
  
  const labels = [
    { label: "Turbulent", corner: 'top-left' as const },
    { label: "Energetic", corner: 'top-right' as const },
    { label: "Melancholic", corner: 'bottom-left' as const },
    { label: "Peaceful", corner: 'bottom-right' as const }
  ];
  
  labels.forEach(item => {
    // Measure text to calculate pill size
    const metrics = ctx.measureText(item.label);
    const pillWidth = metrics.width + bgPadding * 2;
    const pillHeight = labelFontSize + bgPadding * 2;
    
    // Calculate pill position based on corner
    let pillX: number, pillY: number;
    
    switch (item.corner) {
      case 'top-left':
        pillX = heatmapX + distance;
        pillY = heatmapY + distance;
        break;
      case 'top-right':
        pillX = heatmapX + heatmapSize - distance - pillWidth;
        pillY = heatmapY + distance;
        break;
      case 'bottom-left':
        pillX = heatmapX + distance;
        pillY = heatmapY + heatmapSize - distance - pillHeight;
        break;
      case 'bottom-right':
        pillX = heatmapX + heatmapSize - distance - pillWidth;
        pillY = heatmapY + heatmapSize - distance - pillHeight;
        break;
    }
    
    // Draw pill background
    ctx.fillStyle = darkMode ? 'rgba(27, 27, 48, 0.92)' : 'rgba(255, 255, 255, 0.95)';
    ctx.strokeStyle = darkMode ? 'rgba(255,255,255,0.15)' : '#d0d0d0';
    ctx.lineWidth = 2;
    ctx.beginPath();
    ctx.roundRect(pillX, pillY, pillWidth, pillHeight, pillRadius);
    ctx.fill();
    ctx.stroke();

    // Draw text centered in pill
    ctx.fillStyle = darkMode ? '#94A3B8' : '#555555';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(item.label, pillX + pillWidth / 2, pillY + pillHeight / 2);
  });
  
  // Content below heatmap card - MORE spacing and BIGGER text
  currentY += heatmapCardHeight + spaceBetweenSections;
  
  // Use heatmap card width as the constraint for all content below
  const contentMaxWidth = heatmapCardWidth;
  const contentCenterX = width / 2;
  
  // Dominant mood below heatmap - BIGGER
  if (analysis) {
    ctx.fillStyle = darkMode ? '#F8FAFC' : '#0F172A';
    ctx.font = `700 ${isMobile ? 60 : 90}px Inter, Helvetica, Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(`Dominant Mood: ${analysis.quadrant}`, contentCenterX, currentY);
    currentY += (isMobile ? 65 : 95);
    
    // Add summary description below dominant mood - BIGGER
    ctx.fillStyle = darkMode ? '#94A3B8' : 'rgba(0, 0, 0, 0.7)';
    ctx.font = `400 ${isMobile ? 32 : 50}px Inter, Helvetica, Arial, sans-serif`;
    ctx.textAlign = 'center';
    
    // Create shorter summary without song examples
    const summaryText = `${analysis.description}. ${analysis.diversityDescription}.`;
    
    // Word wrap the summary text - constrained to heatmap card width with padding
    const maxWidth = contentMaxWidth - (isMobile ? 100 : 140);
    const words = summaryText.split(' ');
    let line = '';
    const lines: string[] = [];
    
    for (let i = 0; i < words.length; i++) {
      const testLine = line + words[i] + ' ';
      const metrics = ctx.measureText(testLine);
      
      if (metrics.width > maxWidth && i > 0) {
        lines.push(line.trim());
        line = words[i] + ' ';
      } else {
        line = testLine;
      }
    }
    lines.push(line.trim());
    
    // Draw each line centered - BIGGER line height
    const lineHeight = isMobile ? 46 : 64;
    lines.forEach((textLine, index) => {
      ctx.fillText(textLine, contentCenterX, currentY + (index * lineHeight));
    });
    
    currentY += (lines.length * lineHeight) + (isMobile ? 65 : 90);
  }
  
  // Footer row: User on left, "Discover more at" on right - use already defined footerFontSize
  const footerLeftX = heatmapCardX;
  const footerRightX = heatmapCardX + heatmapCardWidth;
  
  ctx.font = `400 ${footerFontSize}px Inter, Helvetica, Arial, sans-serif`;
  const userName = user?.name || '';
  const appDomain = config.app.baseUri.replace(/^https?:\/\//, '');
  const rightText = `Discover more at ${appDomain}`;
  
  // Calculate baseline Y position for text alignment
  const footerBaselineY = currentY;
  
  // User info on the left with profile picture (if available)
  if (user && userName) {
    const profilePicSize = isMobile ? 44 : 60;
    const profilePicPadding = 12;
    
    // Load and draw profile picture if available
    if (user.imageUrl) {
      try {
        const img = new Image();
        img.crossOrigin = 'anonymous';
        
        await new Promise<void>((resolve, reject) => {
          img.onload = () => {
            // Calculate vertical center position for profile picture to align with text baseline
            const picCenterY = footerBaselineY - footerFontSize * 0.35; // Align with text middle
            
            // Draw circular profile picture
            ctx.save();
            ctx.beginPath();
            ctx.arc(
              footerLeftX + profilePicSize / 2,
              picCenterY,
              profilePicSize / 2,
              0,
              Math.PI * 2
            );
            ctx.closePath();
            ctx.clip();
            
            ctx.drawImage(
              img,
              footerLeftX,
              picCenterY - profilePicSize / 2,
              profilePicSize,
              profilePicSize
            );
            ctx.restore();
            
            // Draw border around profile picture
            ctx.strokeStyle = 'rgba(0,0,0,0.1)';
            ctx.lineWidth = 2;
            ctx.beginPath();
            ctx.arc(
              footerLeftX + profilePicSize / 2,
              picCenterY,
              profilePicSize / 2,
              0,
              Math.PI * 2
            );
            ctx.stroke();
            
            resolve();
          };
          img.onerror = () => {
            // If image fails to load, just skip it
            resolve();
          };
          img.src = user.imageUrl;
        });
        
        // Draw username next to profile picture, aligned to baseline
        ctx.fillStyle = darkMode ? '#64748B' : '#666666';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'alphabetic';
        ctx.fillText(userName, footerLeftX + profilePicSize + profilePicPadding, footerBaselineY);
      } catch (err) {
        // If anything fails, just show username without picture
        ctx.fillStyle = darkMode ? '#64748B' : '#666666';
        ctx.textAlign = 'left';
        ctx.textBaseline = 'alphabetic';
        ctx.fillText(userName, footerLeftX, footerBaselineY);
      }
    } else {
      // No profile picture, just show username
      ctx.fillStyle = darkMode ? '#64748B' : '#666666';
      ctx.textAlign = 'left';
      ctx.textBaseline = 'alphabetic';
      ctx.fillText(userName, footerLeftX, footerBaselineY);
    }
  }
  
  // "Discover more at" text, centred
  ctx.fillStyle = darkMode ? '#64748B' : '#666666';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'alphabetic';
  ctx.fillText(rightText, width / 2, footerBaselineY);
  
  return new Promise((resolve, reject) => {
    canvas.toBlob(async (blob) => {
      if (!blob) {
        reject(new Error('Failed to generate image'));
        return;
      }
      
      const file = new File([blob], `emotify-${currentTimeRange}-${Date.now()}.png`, { type: 'image/png' });
      
      // If action is download, skip sharing and go straight to download
      if (action === 'download') {
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.download = `emotify-${currentTimeRange}-${Date.now()}.png`;
        link.href = url;
        link.click();
        URL.revokeObjectURL(url);
        sendTelemetryEvent({ eventType: 'heatmap_shared', platform: musicPlatform as any, method: action });
        resolve();
        return;
      }
      
      // Try Web Share API for share action
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          const shareText = viewMode === 'playlist' && playlistName
            ? `Check out my music taste on emotify.dev! Playlist: ${playlistName}`
            : `Check out my music taste on emotify.dev! ${timeRangeLabel}`;

          // At share time the user is on /results/:playlistId, so the current URL
          // is the re-hydratable deep link — share it alongside the image so the
          // recipient can open the interactive heatmap, not just the screenshot.
          const shareUrl = window.location.href.includes('/results/')
            ? window.location.href
            : config.app.baseUri;

          await navigator.share({
            files: [file],
            title: "My Music's Emotional Landscape",
            text: shareText,
            url: shareUrl,
          });
          sendTelemetryEvent({ eventType: 'heatmap_shared', platform: musicPlatform as any, method: action });
          resolve();
          return;
        } catch (err) {
          if ((err as Error).name === 'AbortError') {
            resolve(); // User cancelled
            return;
          }
        }
      }
      
      // Fallback to download if sharing not available
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `emotify-${currentTimeRange}-${Date.now()}.png`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      sendTelemetryEvent({ eventType: 'heatmap_shared', platform: musicPlatform as any, method: action });
      resolve();
    }, 'image/png');
  });
};

/**
 * Generate a square playlist-cover image from the heatmap: full-bleed art with a
 * small emotify.dev watermark, sized for direct upload as a Spotify/YouTube Music
 * playlist cover.
 */
export const generatePlaylistCover = async (
  sourceCanvas: HTMLCanvasElement,
  darkMode: boolean = false,
  musicPlatform?: string
): Promise<void> => {
  const size = 1400;

  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Could not get canvas context');

  // Background + full-bleed heatmap
  ctx.fillStyle = darkMode ? '#1B1B30' : '#ffffff';
  ctx.fillRect(0, 0, size, size);
  ctx.drawImage(sourceCanvas, 0, 0, size, size);

  // Subtle crosshairs, matching the in-app heatmap
  const center = size / 2;
  ctx.strokeStyle = darkMode ? 'rgba(255,255,255,0.18)' : 'rgba(0,0,0,0.14)';
  ctx.lineWidth = 3;
  ctx.setLineDash([14, 14]);
  ctx.beginPath();
  ctx.moveTo(center, 0);
  ctx.lineTo(center, size);
  ctx.moveTo(0, center);
  ctx.lineTo(size, center);
  ctx.stroke();
  ctx.setLineDash([]);

  // emotify.dev watermark pill, bottom-right corner
  const watermark = 'emotify.dev';
  const fontSize = 34;
  const paddingX = 22;
  const paddingY = 14;
  const margin = 36;
  ctx.font = `700 ${fontSize}px Inter, Helvetica, Arial, sans-serif`;
  const metrics = ctx.measureText(watermark);
  const pillWidth = metrics.width + paddingX * 2;
  const pillHeight = fontSize + paddingY * 2;
  const pillX = size - pillWidth - margin;
  const pillY = size - pillHeight - margin;

  ctx.fillStyle = darkMode ? 'rgba(15,15,30,0.78)' : 'rgba(255,255,255,0.85)';
  ctx.strokeStyle = darkMode ? 'rgba(255,255,255,0.16)' : 'rgba(0,0,0,0.1)';
  ctx.lineWidth = 2;
  ctx.beginPath();
  ctx.roundRect(pillX, pillY, pillWidth, pillHeight, pillHeight / 2);
  ctx.fill();
  ctx.stroke();

  ctx.fillStyle = darkMode ? '#F8FAFC' : '#0F172A';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(watermark, pillX + pillWidth / 2, pillY + pillHeight / 2 + 2);

  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (!blob) {
        reject(new Error('Failed to generate playlist cover'));
        return;
      }
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `emotify-playlist-cover-${Date.now()}.png`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      sendTelemetryEvent({ eventType: 'heatmap_shared', platform: musicPlatform as any, method: 'cover' });
      resolve();
    }, 'image/png');
  });
};

/**
 * Generate and share/download a video
 */
export const generateVideo = async (
  precomputedData: { [key: string]: { heatmapData: HeatmapData } },
  timeRangeOptions: TimeRangeOption[],
  user: { name: string; imageUrl: string } | null | undefined,
  onTimeRangeChange: (timeRange: string) => void
): Promise<void> => {
  const { width, height } = getExportDimensions('video');
  
  const exportCanvas = document.createElement('canvas');
  exportCanvas.width = width;
  exportCanvas.height = height;
  const ctx = exportCanvas.getContext('2d');
  
  if (!ctx) throw new Error('Could not get canvas context');
  
  // Setup video recording with codec fallback
  const stream = exportCanvas.captureStream(30); // 30 FPS
  
  // Try different codecs in order of preference
  const codecOptions = [
    { mimeType: 'video/webm;codecs=vp9', ext: 'webm' },
    { mimeType: 'video/webm;codecs=vp8', ext: 'webm' },
    { mimeType: 'video/webm', ext: 'webm' },
    { mimeType: 'video/mp4', ext: 'mp4' }
  ];
  
  let selectedCodec = codecOptions[0];
  for (const codec of codecOptions) {
    if (MediaRecorder.isTypeSupported(codec.mimeType)) {
      selectedCodec = codec;
      break;
    }
  }
  
  const mediaRecorder = new MediaRecorder(stream, {
    mimeType: selectedCodec.mimeType,
    videoBitsPerSecond: 5000000 // 5 Mbps
  });
  
  const chunks: Blob[] = [];
  
  return new Promise((resolve, reject) => {
    mediaRecorder.ondataavailable = (e) => {
      if (e.data.size > 0) {
        chunks.push(e.data);
      }
    };
    
    mediaRecorder.onstop = async () => {
      const blob = new Blob(chunks, { type: selectedCodec.mimeType });
      const file = new File([blob], `emotify-evolution-${Date.now()}.${selectedCodec.ext}`, { type: selectedCodec.mimeType });
      
      // Try Web Share API first
      if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
        try {
          await navigator.share({
            files: [file],
            title: 'My Music Taste Evolution',
            text: 'Watch how my music taste evolved over time on Emotify!',
            url: window.location.href.includes('/results/') ? window.location.href : config.app.baseUri,
          });
          resolve();
          return;
        } catch (err) {
          if ((err as Error).name === 'AbortError') {
            resolve();
            return;
          }
        }
      }
      
      // Fallback to download
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.download = `emotify-evolution-${Date.now()}.${selectedCodec.ext}`;
      link.href = url;
      link.click();
      URL.revokeObjectURL(url);
      resolve();
    };
    
    mediaRecorder.onerror = (e) => {
      reject(new Error('MediaRecorder error'));
    };
    
    mediaRecorder.start();
    
    // Animation sequence with smooth transitions
    const sequence = ['long_term', 'medium_term', 'short_term'].filter(key => precomputedData[key]);
    const fps = 30;
    const holdDuration = 2000; // 2 seconds hold
    const transitionDuration = 1500; // 1.5 seconds transition
    
    let currentIndex = 0;
    let frameCount = 0;
    let isTransitioning = false;
    let transitionProgress = 0;
    
    const holdFrames = Math.floor(holdDuration / (1000 / fps));
    const transitionFrames = Math.floor(transitionDuration / (1000 / fps));
    
    // Bilinear interpolation helper
    const bilinearInterpolation = (matrix: number[][], x: number, y: number, gridSize: number): number => {
      const x1 = Math.floor(x);
      const x2 = Math.min(x1 + 1, gridSize - 1);
      const y1 = Math.floor(y);
      const y2 = Math.min(y1 + 1, gridSize - 1);
      
      if (x1 < 0 || y1 < 0 || x1 >= gridSize || y1 >= gridSize) return 0;
      
      const dx = x - x1;
      const dy = y - y1;
      
      const v11 = matrix[y1]?.[x1] || 0;
      const v12 = matrix[y2]?.[x1] || 0;
      const v21 = matrix[y1]?.[x2] || 0;
      const v22 = matrix[y2]?.[x2] || 0;
      
      return Math.max(0, v11 * (1 - dx) * (1 - dy) + v21 * dx * (1 - dy) + v12 * (1 - dx) * dy + v22 * dx * dy);
    };
    
    // Color scale function (matching Heatmap.tsx)
    const getColor = (value: number, maxValue: number): string => {
      if (value <= 0.001) return "rgba(255, 255, 255, 0)";
      
      const colors = [
        [255, 255, 255], // Pure white
        [235, 235, 255], // Very light purple
        [210, 210, 255], // Light purple
        [170, 190, 255], // Bright blue
        [130, 210, 240], // Bright cyan
        [140, 240, 160], // Bright green
        [210, 250, 120], // Bright yellow-green
        [255, 240, 100], // Bright yellow
        [255, 200, 100], // Bright orange
        [255, 150, 110], // Brighter coral
        [255, 100, 100]  // Much brighter red
      ];
      
      const scaledT = Math.min(1, Math.max(0, value / maxValue));
      const index = scaledT * (colors.length - 1);
      const lower = Math.floor(index);
      const upper = Math.ceil(index);
      const fraction = index - lower;
      
      if (lower === upper) {
        const [r, g, b] = colors[lower];
        const opacity = Math.pow(scaledT, 0.5) * 0.95;
        return `rgba(${r}, ${g}, ${b}, ${opacity})`;
      }
      
      const [r1, g1, b1] = colors[lower];
      const [r2, g2, b2] = colors[upper];
      const r = Math.round(r1 + (r2 - r1) * fraction);
      const g = Math.round(g1 + (g2 - g1) * fraction);
      const b = Math.round(b1 + (b2 - b1) * fraction);
      
      const opacity = Math.pow(scaledT, 0.5) * 0.95;
      return `rgba(${r}, ${g}, ${b}, ${opacity})`;
    };
    
    // Render a single frame
    const renderFrame = (matrix1: number[][], matrix2: number[][] | null, progress: number, label: string) => {
      const isMobile = height > width;
      const titleY = isMobile ? 100 : 70;
      const subtitleY = isMobile ? 150 : 110;
      const heatmapSize = isMobile ? Math.min(width - 80, 900) : 800;
      const heatmapX = (width - heatmapSize) / 2;
      const heatmapY = isMobile ? 200 : 150;
      
      // Clear background
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, width, height);
      
      // Add title
      ctx.fillStyle = '#000000';
      ctx.font = `bold ${isMobile ? 48 : 40}px Inter, Helvetica, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText("My Music's Emotional Landscape", width / 2, titleY);
      
      // Add subtitle
      ctx.font = `${isMobile ? 32 : 28}px Inter, Helvetica, Arial, sans-serif`;
      ctx.fillStyle = '#666666';
      ctx.fillText(label, width / 2, subtitleY);
      
      // Render heatmap with high quality
      const gridSize = matrix1.length;
      const smoothMultiplier = 8;
      const smoothGridSize = gridSize * smoothMultiplier;
      const cellSize = heatmapSize / smoothGridSize;
      
      // Calculate max value for color scaling
      const flatMatrix1 = matrix1.flat().filter((val: any) => typeof val === 'number') as number[];
      const maxValue = flatMatrix1.length > 0 ? (Math.max(...flatMatrix1) || 1) : 1;
      
      for (let i = 0; i < smoothGridSize; i++) {
        const origI = (i / (smoothGridSize - 1)) * (gridSize - 1);
        const flippedOrigI = (gridSize - 1) - origI;
        
        for (let j = 0; j < smoothGridSize; j++) {
          const origJ = (j / (smoothGridSize - 1)) * (gridSize - 1);
          
          let value = bilinearInterpolation(matrix1, origJ, flippedOrigI, gridSize);
          
          // Interpolate between matrices if transitioning
          if (matrix2 && progress < 1) {
            const value2 = bilinearInterpolation(matrix2, origJ, flippedOrigI, gridSize);
            value = value * (1 - progress) + value2 * progress;
          }
          
          if (value <= 0.0001) continue;
          
          const x = heatmapX + (j / smoothGridSize) * heatmapSize;
          const y = heatmapY + (i / smoothGridSize) * heatmapSize;
          
          ctx.fillStyle = getColor(value, maxValue);
          ctx.fillRect(x, y, Math.ceil(cellSize) + 1, Math.ceil(cellSize) + 1);
        }
      }
      
      // Draw border box with rounded corners
      ctx.strokeStyle = '#cccccc';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.roundRect(heatmapX, heatmapY, heatmapSize, heatmapSize, 16);
      ctx.stroke();
      
      // Draw crosshairs
      const centerX = heatmapX + heatmapSize / 2;
      const centerY = heatmapY + heatmapSize / 2;
      
      ctx.strokeStyle = '#999999';
      ctx.lineWidth = 1;
      ctx.setLineDash([5, 5]);
      
      ctx.beginPath();
      ctx.moveTo(centerX, heatmapY);
      ctx.lineTo(centerX, heatmapY + heatmapSize);
      ctx.stroke();
      
      ctx.beginPath();
      ctx.moveTo(heatmapX, centerY);
      ctx.lineTo(heatmapX + heatmapSize, centerY);
      ctx.stroke();
      
      ctx.setLineDash([]);
      
      // Add quadrant labels
      const labelPadding = 20;
      const labelFontSize = isMobile ? 20 : 18;
      ctx.font = `600 ${labelFontSize}px Inter, Helvetica, Arial, sans-serif`;
      
      const quadrants = [
        { label: "Turbulent", x: heatmapX + labelPadding, y: heatmapY + labelPadding + labelFontSize },
        { label: "Energetic", x: heatmapX + heatmapSize - labelPadding, y: heatmapY + labelPadding + labelFontSize, align: 'right' as const },
        { label: "Melancholic", x: heatmapX + labelPadding, y: heatmapY + heatmapSize - labelPadding },
        { label: "Peaceful", x: heatmapX + heatmapSize - labelPadding, y: heatmapY + heatmapSize - labelPadding, align: 'right' as const }
      ];
      
      quadrants.forEach(q => {
        ctx.textAlign = q.align || 'left';
        
        const metrics = ctx.measureText(q.label);
        const bgPadding = 10;
        const bgX = q.align === 'right' ? q.x - metrics.width - bgPadding * 2 : q.x - bgPadding;
        const bgY = q.y - labelFontSize - bgPadding;
        const bgWidth = metrics.width + bgPadding * 2;
        const bgHeight = labelFontSize + bgPadding * 2;
        
        ctx.fillStyle = 'rgba(255, 255, 255, 0.95)';
        ctx.strokeStyle = '#d0d0d0';
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.roundRect(bgX, bgY, bgWidth, bgHeight, 6);
        ctx.fill();
        ctx.stroke();
        
        ctx.fillStyle = '#555555';
        ctx.fillText(q.label, q.x, q.y);
      });
      
      // Add user info in bottom left and "Discover more at" in bottom right
      const footerY = isMobile ? height - 60 : height - 40;
      const footerPadding = 40;
      
      if (user) {
        // For video, we'll just show the name (loading images async is complex in video frames)
        ctx.fillStyle = '#666666';
        ctx.font = `${isMobile ? 20 : 16}px Inter, Helvetica, Arial, sans-serif`;
        ctx.textAlign = 'left';
        ctx.fillText(user.name, footerPadding, footerY);
      }
      
      // "Discover more at" centred
      const appDomain = config.app.baseUri.replace(/^https?:\/\//, '');
      ctx.fillStyle = '#999999';
      ctx.font = `${isMobile ? 20 : 16}px Inter, Helvetica, Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.fillText(`Discover more at ${appDomain}`, width / 2, footerY);
    };
    
    const renderNextFrame = () => {
      if (currentIndex >= sequence.length) {
        // Animation complete
        setTimeout(() => {
          mediaRecorder.stop();
        }, 100);
        return;
      }
      
      const timeRangeKey = sequence[currentIndex];
      const data = precomputedData[timeRangeKey];
      const label = timeRangeOptions.find(opt => opt.key === timeRangeKey)?.label || '';
      
      if (!isTransitioning) {
        // Hold phase
        renderFrame(data.heatmapData.matrix, null, 1, label);
        frameCount++;
        
        // Skip hold for medium_term (go straight to transition)
        const shouldHold = timeRangeKey !== 'medium_term';
        
        if (!shouldHold || frameCount >= holdFrames) {
          // Start transition to next
          if (currentIndex < sequence.length - 1) {
            isTransitioning = true;
            transitionProgress = 0;
            frameCount = 0;
          } else {
            currentIndex++;
          }
        }
      } else {
        // Transition phase
        const nextTimeRangeKey = sequence[currentIndex + 1];
        const nextData = precomputedData[nextTimeRangeKey];
        const nextLabel = timeRangeOptions.find(opt => opt.key === nextTimeRangeKey)?.label || '';
        
        if (nextData) {
          const progress = transitionProgress / transitionFrames;
          const easedProgress = progress < 0.5 
            ? 2 * progress * progress 
            : 1 - Math.pow(-2 * progress + 2, 2) / 2; // easeInOutQuad
          
          renderFrame(data.heatmapData.matrix, nextData.heatmapData.matrix, easedProgress, nextLabel);
          
          transitionProgress++;
          
          if (transitionProgress >= transitionFrames) {
            // Transition complete
            isTransitioning = false;
            currentIndex++;
            frameCount = 0;
            transitionProgress = 0;
          }
        } else {
          currentIndex++;
          isTransitioning = false;
        }
      }
      
      // Schedule next frame
      setTimeout(renderNextFrame, 1000 / fps);
    };
    
    // Start rendering
    renderNextFrame();
  });
};
